import type { AnalysisResult } from "@/components/ContractAnalyzer";

/**
 * CJK-safe PDF export.
 *
 * jsPDF's built-in Helvetica font cannot render Chinese / Japanese / Korean
 * characters (they show as blank or "????"), and bundling a full Noto Sans CJK
 * font adds ~10MB to the client bundle. Instead we render the report into a
 * hidden iframe with a print-optimized HTML/CSS template and trigger
 * `window.print()` — the browser handles Unicode natively and the user can
 * "Save as PDF" from the system print dialog, which produces a clean, fully
 * styled report that supports every language.
 */

type ExportOpts = { title?: string; toolSlug?: string | null };

function esc(s: unknown): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

const FONT_LINKS = `
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Noto+Sans:wght@400;600;700&family=Noto+Sans+SC:wght@400;600;700&family=Noto+Sans+TC:wght@400;600;700&family=Noto+Sans+JP:wght@400;600;700&family=Noto+Sans+KR:wght@400;600;700&display=swap" rel="stylesheet">
`;

const BASE_CSS = `
  * { box-sizing: border-box; }
  html, body {
    margin: 0;
    padding: 0;
    font-family: "Noto Sans", "Noto Sans SC", "Noto Sans TC", "Noto Sans JP",
                 "Noto Sans KR", "PingFang SC", "Hiragino Sans GB",
                 "Microsoft YaHei", -apple-system, BlinkMacSystemFont, sans-serif;
    color: #1a202c;
    background: #fff;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .page { padding: 40px 48px; max-width: 780px; margin: 0 auto; }
  .header {
    background: #4a5568; color: #fff; padding: 20px 48px;
    display: flex; justify-content: space-between; align-items: center;
    margin: -40px -48px 28px;
  }
  .header h1 { margin: 0; font-size: 22px; font-weight: 700; letter-spacing: -0.01em; }
  .header .tag { font-size: 12px; opacity: 0.9; }
  h2 { font-size: 16px; font-weight: 700; margin: 24px 0 10px; color: #2d3748; }
  h3 { font-size: 13px; font-weight: 700; margin: 16px 0 6px; color: #2d3748; }
  p { font-size: 12px; line-height: 1.6; margin: 6px 0; color: #4a5568; }
  ul { margin: 8px 0; padding-left: 20px; }
  li { font-size: 12px; line-height: 1.6; color: #4a5568; margin: 4px 0; }
  .meta { font-size: 11px; color: #718096; margin-bottom: 4px; }
  .banner {
    border-radius: 8px; padding: 12px 16px; margin: 12px 0 18px;
    font-weight: 600; font-size: 13px;
  }
  .banner.high   { background: #fee2e2; color: #991b1b; }
  .banner.medium { background: #fef3c7; color: #78350f; }
  .banner.low    { background: #dcfce7; color: #166534; }
  .clause {
    border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px 14px; margin: 10px 0;
    page-break-inside: avoid;
  }
  .clause .title { font-weight: 700; font-size: 13px; color: #1a202c; margin-bottom: 6px; }
  .clause .sev { font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em; padding: 2px 8px; border-radius: 999px; margin-left: 6px; }
  .sev.warning { background: #fee2e2; color: #991b1b; }
  .sev.caution { background: #fef3c7; color: #78350f; }
  .sev.info, .sev.ok { background: #dcfce7; color: #166534; }
  .label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.06em; color: #718096; margin-top: 8px; font-weight: 700; }
  .red-flag { color: #991b1b; }
  .privacy {
    margin-top: 28px; padding-top: 16px; border-top: 1px solid #e2e8f0;
    font-size: 11px; color: #718096; line-height: 1.55;
  }
  .footer-note {
    margin-top: 18px; text-align: center; font-size: 10px; color: #a0aec0;
    font-style: italic; letter-spacing: 0.03em;
  }
  @page { size: letter; margin: 14mm; }
  @media print {
    .header { margin: 0 0 20px; border-radius: 0; }
    .page { padding: 0; }
    .no-print { display: none !important; }
  }
  .toolbar {
    position: fixed; top: 12px; right: 12px; display: flex; gap: 8px; z-index: 10;
  }
  .toolbar button {
    background: #4a5568; color: #fff; border: none; padding: 8px 14px;
    border-radius: 6px; font-size: 13px; cursor: pointer; font-weight: 600;
  }
  .toolbar button.secondary { background: #e2e8f0; color: #2d3748; }
`;

function renderAnalysisHtml(
  result: AnalysisResult,
  opts: ExportOpts,
  mode: "full" | "lawyer",
): string {
  const risk = result.verdict.risk;
  const bannerLabel =
    mode === "lawyer" && risk === "high"
      ? "HIGH RISK — attorney review recommended"
      : risk === "high"
        ? "HIGH RISK"
        : risk === "medium"
          ? "MEDIUM RISK"
          : "LOW RISK";

  const title = opts.title ? `<h2>${esc(opts.title)}</h2>` : "";
  const toolSlug = opts.toolSlug
    ? `<div class="meta">Document type: ${esc(opts.toolSlug)}</div>`
    : "";

  const keyTerms = result.keyTerms?.length
    ? `<h2>Key terms</h2><ul>${result.keyTerms.map((t) => `<li>${esc(t)}</li>`).join("")}</ul>`
    : "";

  const redFlags = result.redFlags?.length
    ? `<h2>Red flags</h2><ul>${result.redFlags.map((f) => `<li class="red-flag">${esc(f)}</li>`).join("")}</ul>`
    : "";

  const clauses = result.clauseExplanations?.length
    ? `<h2>${mode === "lawyer" ? "Clauses worth a closer look" : "Clause-by-clause breakdown"}</h2>` +
      result.clauseExplanations
        .map(
          (c) => `
        <div class="clause">
          <div class="title">${esc(c.title)}<span class="sev ${esc(c.severity)}">${esc(c.severity)}</span></div>
          <div class="label">What it says</div><p>${esc(c.what)}</p>
          <div class="label">Why it matters</div><p>${esc(c.why)}</p>
          <div class="label">What to do</div><p>${esc(c.suggestion)}</p>
        </div>`,
        )
        .join("")
    : "";

  const lawyerQuestions =
    mode === "lawyer"
      ? `<h2>Suggested questions for your attorney</h2>
        <ul>
          <li>Are any of the flagged clauses unenforceable in my state?</li>
          <li>What's a fair counter-proposal for the highest-risk terms?</li>
          <li>What's my realistic exposure if I sign as-is?</li>
          <li>Are there missing clauses (indemnity, dispute resolution, etc.) I should request?</li>
        </ul>`
      : "";

  const headerTag =
    mode === "lawyer" ? "For your attorney — contract risk briefing" : "Contract Analysis Report";

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>legaldecoder — ${esc(opts.title ?? headerTag)}</title>
${FONT_LINKS}
<style>${BASE_CSS}</style>
</head>
<body>
  <div class="toolbar no-print">
    <button onclick="window.print()">Save as PDF / Print</button>
    <button class="secondary" onclick="window.close()">Close</button>
  </div>
  <div class="page">
    <div class="header">
      <h1>legaldecoder</h1>
      <div class="tag">${esc(headerTag)}</div>
    </div>
    ${title}
    ${toolSlug}
    <div class="banner ${esc(risk)}">${esc(bannerLabel)} · Score ${esc(result.riskScore)}/100</div>
    <p>${esc(result.verdict.summary)}</p>
    ${redFlags}
    ${keyTerms}
    ${clauses}
    ${lawyerQuestions}
    <div class="privacy">
      <strong>Privacy notice.</strong> This report was generated by legaldecoder using a
      zero-data-retention pipeline. The contract text was processed in-memory and was
      not stored on our servers or used to train AI models. End-to-end encryption
      protects all uploads.
    </div>
    <div class="footer-note">NOT LEGAL ADVICE. FOR INFORMATIONAL PURPOSES ONLY. · legaldecoder.io</div>
  </div>
  <script>
    // Auto-open the print dialog once webfonts have loaded so CJK glyphs render correctly.
    (function() {
      function go() { setTimeout(function() { try { window.focus(); window.print(); } catch (e) {} }, 350); }
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(go).catch(go);
      } else {
        window.addEventListener('load', go);
      }
    })();
  </script>
</body>
</html>`;
}

function openPrintWindow(html: string) {
  // Prefer a new window so the user keeps the analyzer tab intact. Fall back to a
  // hidden iframe if popup blockers intervene.
  const w =
    typeof window !== "undefined" ? window.open("", "_blank", "width=900,height=1100") : null;
  if (w) {
    w.document.open();
    w.document.write(html);
    w.document.close();
    return;
  }
  // Fallback: hidden iframe
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  document.body.appendChild(iframe);
  const doc = iframe.contentDocument;
  if (!doc) return;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    try {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } catch {
      /* noop */
    }
    setTimeout(() => iframe.remove(), 60_000);
  }, 600);
}

export async function exportAnalysisPdf(result: AnalysisResult, opts?: ExportOpts) {
  openPrintWindow(renderAnalysisHtml(result, opts ?? {}, "full"));
}

export async function exportLawyerReport(result: AnalysisResult, opts?: ExportOpts) {
  openPrintWindow(renderAnalysisHtml(result, opts ?? {}, "lawyer"));
}
